import React, { useState, useCallback, useRef } from 'react';
import { UploadCloud } from 'lucide-react';

interface InitialDropzoneProps {
  onDropFiles: (files: File[]) => void;
}

export const InitialDropzone: React.FC<InitialDropzoneProps> = ({
  onDropFiles,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    // FIX: Cast `e.dataTransfer` to `any` to access the `files` property.
    const dataTransfer = e.dataTransfer as any;
    if (dataTransfer.files && dataTransfer.files.length > 0) {
      onDropFiles(Array.from(dataTransfer.files));
    }
  }, [onDropFiles]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    // FIX: Cast `e.target` to `HTMLInputElement` to access the `files` property.
    const target = e.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      onDropFiles(Array.from(target.files));
      // Reset input value to allow selecting the same file again
      // FIX: Cast `e.target` to `HTMLInputElement` to access the `value` property.
      (e.target as HTMLInputElement).value = '';
    }
  };

  const handleClick = () => {
    // FIX: Cast `fileInputRef.current` to `any` to call the `click` method.
    (fileInputRef.current as any)?.click();
  };

  return (
    <div className="w-full h-full flex flex-col gap-4">
      <div
        className={`w-full flex-1 bg-black text-white rounded-2xl border border-gray-800 shadow-2xl flex flex-col items-center p-8 transition-colors duration-300 cursor-pointer overflow-y-auto ${isDragOver ? 'bg-gray-900' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label="Zone de dépôt de fichiers. Glissez-déposez ou cliquez pour ajouter des médias."
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleClick()}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          className="hidden"
          accept="video/*,image/*,audio/*"
          aria-hidden="true"
        />
        <div
          className={`w-full min-h-full rounded-2xl border-4 border-dashed transition-all duration-300 flex flex-col items-center justify-center text-center p-8 pointer-events-none
            ${isDragOver ? 'border-blue-400' : 'border-gray-600'}`}
        >
          <UploadCloud className={`w-16 h-16 mb-4 transition-colors duration-300 ${isDragOver ? 'text-blue-300' : 'text-gray-400'}`} />
          <h2 className={`text-2xl font-bold transition-colors duration-300 ${isDragOver ? 'text-white' : 'text-gray-200'}`}>
            Glissez-déposez vos médias ici
          </h2>
          <p className="text-gray-400 mt-2 max-w-md">
            ou <span className="text-blue-400 font-semibold">cliquez pour parcourir</span>
          </p>
          <p className="text-gray-500 mt-4 text-sm max-w-md">
            Les sous-titres et suggestions d'édition seront générés automatiquement pour les vidéos.
          </p>
        </div>
      </div>
    </div>
  );
};
